/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Card, Checkbox, Dropdown, Form, Input, Row, Select, Space, Spin, Table } from 'antd';
import CustomRangePicker from '../Forms/Fields/CustomRangePicker';
import { SearchOutlined } from '@ant-design/icons';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { AUDIT_LOG_CATEGORIES, AUDIT_LOG_RETENTION_OPTIONS, DEFAULT_DATETIME_FORMAT, getPresetDateRanges, DATETIME_BACKEND_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { useFetchOrganizationQuery, useUpdateOrganizationMutation } from '../../store/organization/organizationApi';
import { useFetchUsersThinListQuery } from '../../store/users/usersApi';
import { useFetchAuditLogsQuery } from '../../store/auditLogs/auditLogsApi';
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

const AuditLogsTable = () => {
  const [form] = Form.useForm();
  const [usersForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [orderedUsers, setOrderedUsers] = useState([]);
  const [orderedUsersEmails, setOrderedUsersEmails] = useState([]);
  const [emailNotificationsDropdownOpen, setEmailNotificationsDropdownOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const [updateOrganization, { isLoading: isUpdatingOrganization }] = useUpdateOrganizationMutation();
  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: usersThinList } = useFetchUsersThinListQuery({ orgId }, { skip: !orgId });
  const { data: auditLogs, isFetching: isFetchingAuditLogs } = useFetchAuditLogsQuery({
    orgId,
    urlParams: {
      page: searchParams.get('page') || currentPage - 1,
      pageSize: pageSize,
      search: searchParams.get('search') || '',
      sortBy: searchParams.get('sortBy') || 'createdAt',
      order: searchParams.get('order') || 'desc',
      startDate: `${searchParams.get('startDate')}${encodeURIComponent(moment().tz(orgTimezone)?.format('Z'))}`,
      endDate: `${searchParams.get('endDate')}${encodeURIComponent(moment().tz(orgTimezone)?.format('Z'))}`,
      ...(searchParams.get('category') && { category: searchParams.get('category') }),
      ...(searchParams.get('userId') && { userId: searchParams.get('userId') })
    }
  }, { skip: !organization || !searchParams.get('startDate') || !searchParams.get('endDate') });

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'createdAt';

    return {
      order,
      columnKey
    };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  const searchInfo = useMemo(() => {
    let search = searchParams.get('search') ? decodeURIComponent(searchParams.get('search')) : null;

    return search;
  }, [searchParams.get('search')]);

  const usersMenu = (
    <Form
      form={usersForm}
      initialValues={{
        users: organization?.auditLogsNotificationEmail || ''
      }}
      onChange={e => {
        const userEmail = usersForm.getFieldValue('users');
        updateOrganization({ orgId, data: { auditLogsNotificationEmail: userEmail, auditLogsNotificationsEnabled: !!userEmail } });
        handleOpenChange(false);
      }}
    >
      <Card className="p-0">
        {
          orderedUsersEmails.length ? (
            <Form.Item
              noStyle
              shouldUpdate
            >
              {
                () => (
                  <Fragment>
                    <div className="mb-1 border-bottom py-2 px-3">
                      {
                        organization?.auditLogsNotificationEmail
                          ? `${t('Selected user to receive email notifications')}: ${organization?.auditLogsNotificationEmail}`
                          : t('Select a Console user to send email notifications')
                      }
                    </div>

                    <div
                      className="d-flex flex-column overflow-y-auto overflow-x-hidden px-3"
                      style={{ maxHeight: 180 }}
                    >
                      {
                        orderedUsersEmails?.map(user => (
                          <Form.Item
                            key={user}
                            className="my-1"
                          >
                            <Checkbox
                              name="users"
                              checked={user === usersForm.getFieldValue('users')}
                              onChange={v => {
                                const val = v.target.checked ? v.target.value : '';
                                usersForm.setFieldValue('users', val);
                              }}
                              value={user}
                            >
                              {user}
                            </Checkbox>
                          </Form.Item>
                        ))
                      }
                    </div>
                  </Fragment>
                )
              }
            </Form.Item>
          ) : (
            <Row justify="center" align="middle" className="p-10"><Spin /></Row>
          )
        }
      </Card>
    </Form>
  );

  useEffect(() => {
    if (usersThinList && usersThinList.length) {
      const dataSource = [...usersThinList];

      setOrderedUsers(dataSource.sort((a, b) => {
        return a.email.localeCompare(b.email);
      }));
    }
  }, [usersThinList]);

  useEffect(() => {
    setOrderedUsersEmails([...new Set(orderedUsers.map(i => i.email))]);
  }, [orderedUsers]);

  useEffect(() => {
    if (organization) {
      updateUrlSearchParams(Number(searchParams.get('page')) + 1, DEFAULT_PAGINATION_SIZE, null, { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'desc' : 'asc' }, searchInfo);
    }
  }, [organization]);

  const updateUrlSearchParams = async (current = 1, pageSize = DEFAULT_PAGINATION_SIZE, filters = { createdBy: [searchParams.get('userId')], category: searchParams.get('category') }, sorting = { createdAt: 'desc' }, initialSearch = null) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set('page', current - 1);

    const search = initialSearch ? initialSearch : form.getFieldValue('search');

    let startDate = searchParams.get('startDate') ? moment.tz(decodeURIComponent(searchParams.get('startDate')), orgTimezone) : moment.tz(moment(), orgTimezone).startOf('day');
    let endDate = searchParams.get('endDate') ? moment.tz(decodeURIComponent(searchParams.get('endDate')), orgTimezone) : moment.tz(moment(), orgTimezone).endOf('day');

    // "hack" to prevent a waring about not initialized form
    if (form.__INTERNAL__.name) {
      const rangePickerArray = form.getFieldValue('rangePicker') || [];
      startDate = rangePickerArray[0];
      endDate = rangePickerArray[1];
    }

    if (filters?.createdBy?.length && filters?.createdBy[0]) {
      urlSearchParams.set('userId', filters?.createdBy[0]);
    } else {
      urlSearchParams.delete('userId');
    }

    if (filters?.category) {
      urlSearchParams.set('category', filters?.category);
    } else {
      urlSearchParams.delete('category');
    }

    if (startDate) {
      const plainDateString = startDate.format(DATETIME_BACKEND_FORMAT);
      urlSearchParams.set('startDate', encodeURIComponent(`${plainDateString}`));
    } else {
      urlSearchParams.delete('startDate');
    }

    if (endDate) {
      const plainDateString = endDate.format(DATETIME_BACKEND_FORMAT);
      urlSearchParams.set('endDate', encodeURIComponent(`${plainDateString}`));
    } else {
      urlSearchParams.delete('endDate');
    }

    if (search) {
      urlSearchParams.set('search', encodeURIComponent(search));
      if (initialSearch) {
        form.setFieldValue('search', initialSearch);
      }
    } else {
      urlSearchParams.set('search', encodeURIComponent(''));
    }

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);

  const handleTableChange = async (pagination, filters, sorter) => {
    let sorting;

    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'createdAt' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }

    updateUrlSearchParams(pagination.current, pagination.pageSize, filters, sorting);
  };

  const filterUserFullNameData = (data = []) => formatter => {
    return data?.map(item => {
      return {
        text: formatter(item)?.text,
        value: formatter(item)?.value
      };
    });
  };

  const columns = [
    {
      title: t('User name'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'createdBy' && sortingInfo.order,
      filters: filterUserFullNameData(orderedUsers)(i => ({
        text: `${i?.fullName} (${i?.email})`,
        value: i?.id
      })),
      filterMultiple: false,
      filteredValue: searchParams.get('userId') ? [searchParams.get('userId')] : null,
      filterIcon: useTableFilterIcon,
      render: createdBy => {
        let userInfo = (usersThinList ?? []).filter((user) => user.id === createdBy.id)[0];
        return userInfo?.fullName;
      }
    },
    {
      title: t('User email'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: createdBy => {
        return createdBy.email;
      }
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      className: 'white-space-pre-wrap'
    },
    {
      title: t('Category'),
      dataIndex: 'category',
      key: 'category',
      className: 'white-space-pre-wrap',
      filteredValue: searchParams.get('category') ? [searchParams.get('category')] : null,
      filters: Object.entries(AUDIT_LOG_CATEGORIES).map(([key, value]) => {
        return {
          text: value,
          value: key
        };
      }),
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      render: category => {
        return AUDIT_LOG_CATEGORIES[category];
      }
    },
    {
      title: t('Date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      render: createdAt => {
        let createdAtTime = moment(createdAt);
        return createdAtTime.tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT);
      }
    }
  ];

  const handleOpenChange = (flag) => {
    setEmailNotificationsDropdownOpen(flag);
  };

  return usersThinList && organization ? (
    <Fragment>
      <Form
        className="w-100 mb-4"
        name="auditLogsDates"
        initialValues={{
          rangePicker: searchParams.get('startDate') && searchParams.get('endDate') ? [
            moment.tz(decodeURIComponent(searchParams.get('startDate')), orgTimezone),
            moment.tz(decodeURIComponent(searchParams.get('endDate')), orgTimezone)
          ] : [moment.tz(moment(), orgTimezone).startOf('day'), moment.tz(moment(), orgTimezone).endOf('day')],
          search: searchParams.get('search') ? decodeURIComponent(searchParams.get('search')) : '',
          auditLogsRetentionPeriod: organization.auditLogsRetentionPeriod || 1
        }}
        form={form}
      >
        <Space wrap align="start" className="justify-content-between w-100">
          <Space wrap align="start">
            {/* Search bar */}
            <Form.Item
              name="search"
              className="mb-0"
            >
              <Input
                size="large"
                placeholder={t('Search in contents')}
                allowClear
                prefix={<SearchOutlined className="text-muted" />}
                value={form.getFieldValue('search')}
                onChange={(e) => {
                  debouncedOnChange();
                  form.setFieldsValue({ search: e.target.value });
                }}
              />
            </Form.Item>
            {/* ./Search bar */}

            {/* Audit logs date range */}
            <Form.Item
              name="rangePicker"
              className="mb-0"
              rules={[{
                required: false,
                message: t('Please select a start date.')
              }]}
            >
              <CustomRangePicker
                disabled={isFetchingAuditLogs}
                size="large"
                disabledDate={(current) => {
                  return current && current > moment.tz(moment(), orgTimezone).endOf('day');
                }}
                allowClear={false}
                onChange={() => {
                  updateUrlSearchParams();
                }}
                ranges={getPresetDateRanges(orgTimezone)}
                timezone={orgTimezone}
              />
            </Form.Item>
            {/* ./Audit logs date range */}
          </Space>

          <Space wrap>
            {/* Audit logs retention period */}
            <Select
              size="large"
              className="fs-base"
              style={{ minWidth: 200 }}
              optionLabelProp="label"
              defaultValue={organization.auditLogsRetentionPeriod}
              placeholder={t('Choose audit log retention period')}
              onChange={
                value => updateOrganization({ orgId, data: { auditLogsRetentionPeriod: value } })
              }
            >
              {
                AUDIT_LOG_RETENTION_OPTIONS.map((month) => {
                  return (
                    <Select.Option key={month.value} value={month.value} label={`${t('Retention')}: ${month.label}`}>
                      {month.label}
                    </Select.Option>
                  );
                })
              }
            </Select>
            {/* ./ Audit logs retention period */}

            {/* Email notifications */}
            <Dropdown
              trigger="click"
              onOpenChange={handleOpenChange}
              open={emailNotificationsDropdownOpen}
              overlayClassName="w-100"
              overlayStyle={{ maxWidth: 320 }}
              overlay={usersMenu}
            >
              <Button type="primary" size="large" loading={isUpdatingOrganization}>
                <Space>
                  {
                    organization?.auditLogsNotificationEmail
                      ? `${t('Email Notifications')} (1)`
                      : `${t('Email Notifications')}`
                  }
                  <ArrowDownIcon />
                </Space>
              </Button>
            </Dropdown>
            {/* ./ Email notifications */}
          </Space>
        </Space>
      </Form>

      <Table
        rowKey="createdAt"
        size="small"
        onChange={handleTableChange}
        pagination={{
          total: auditLogs?.total || 0,
          defaultCurrent: 1,
          current: currentPage,
          pageSize: auditLogs?.pageSize || pageSize,
          showSizeChanger: true,
          size: 'default',
          showTotal: (total) => `${t('Total Audit Logs')}: ${total}`
        }}
        dataSource={auditLogs?.items || []}
        columns={columns}
        loading={isFetchingAuditLogs}
      />
    </Fragment>
  ) : null;
};

export default AuditLogsTable;
